import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { matchPath, useLocation } from 'react-router-dom'
import { useStore } from 'zustand'
import { useHypothesis } from '@/features/hypotheses'
import { useViewpointSnapshot } from '@/features/investigations/hooks/useViewpointSnapshot'
import { caseKeys } from '@/hooks/use-case-queries'
import { api } from '@/services/api'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { getSectionForPath } from './sections'
import { RESERVED_CASE_SEGMENTS, type SubView, WORKSPACES, type Workspace } from './workspaces'

export interface Crumb {
  label: string
  /** If provided, this crumb is rendered as a link. Non-leaf crumbs usually have one. */
  href?: string
}

export interface Breadcrumb {
  /** Crumbs rendered before the investigation chip (section / case). */
  crumbs: Crumb[]
  /**
   * Crumbs rendered AFTER the investigation chip. Non-empty only on lens
   * routes (graph / map / timeline) while a viewpoint is active — the path
   * then reads `Cases / case / [chip] / View`.
   */
  viewTail: Crumb[]
  /**
   * The leaf crumb is the active investigation's name — the same text the chip
   * next to it carries. The phone top bar, which has room for one, drops the crumb.
   */
  leafRepeatsChip?: boolean
}

/**
 * Investigation-scoped lens segments under `/cases/:caseId/` — the views that
 * render a working set. When one is open and a viewpoint (transient
 * exploration or active saved investigation) exists, the view crumb moves
 * behind the investigation chip. Case-level tools (Research, OSINT, …) keep a
 * flat path; the chip trails the breadcrumb there as a plain indicator.
 */
const INVESTIGATION_VIEW_SEGMENTS = new Set(['graph', 'explore-graph', 'geolocation', 'time-axis'])

function segmentToLabel(segment: string): string {
  return segment
    .split('-')
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ')
}

/** Convert a URL path segment to the camelCase key used in nav.subviews.* translations. */
function pathToSubviewKey(segment: string): string {
  return segment.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
}

function findWorkspaceAndSubView(
  segment: string
): { workspace: Workspace; subView: SubView } | undefined {
  for (const ws of WORKSPACES) {
    const sv = ws.subViews.find(s => s.path === segment)
    if (sv) return { workspace: ws, subView: sv }
  }
  return undefined
}

/**
 * Read the title of the investigation referenced by the URL (when the path
 * matches `/cases/:caseId/investigations/:investigationId`). Returns
 * `undefined` for any other path so callers can simply ignore the result.
 *
 * Implemented as an always-running hook to satisfy React's rules of hooks —
 * we always subscribe to *some* investigations store (a sentinel when there's
 * no caseId) and select an undefined title in the no-op branches.
 */
function useInvestigationLeafTitle(
  caseId: string | undefined,
  pathname: string
): string | undefined {
  const match = matchPath(
    { path: '/cases/:caseId/investigations/:investigationId', end: false },
    pathname
  )
  const investigationId = match?.params.investigationId
  const storeApi = caseId ? getOrCreateInvestigationsStore({ caseId }) : NOOP_INVESTIGATIONS_STORE
  return useStore(storeApi, s => {
    if (!investigationId) return undefined
    return s.saved.find(i => i.id === investigationId)?.title
  })
}

/**
 * Empty sentinel investigations store used when no caseId is in scope. Lets
 * `useInvestigationLeafTitle` always call `useStore` (rules of hooks) without
 * spinning up real machinery.
 */
const NOOP_INVESTIGATIONS_STORE = getOrCreateInvestigationsStore({
  caseId: '__breadcrumb_noop__',
})

/**
 * Read the title of the search/hypothesis referenced by the URL (when the path
 * matches `/cases/:caseId/search/:hypothesisId`). Returns `undefined` for any
 * other path. Reuses the React Query cache populated by the detail page's
 * `useHypothesis` call, so no extra fetch is incurred.
 */
function useHypothesisLeafTitle(caseId: string | undefined, pathname: string): string | undefined {
  const match = matchPath({ path: '/cases/:caseId/search/:hypothesisId', end: false }, pathname)
  const hypothesisId = match?.params.hypothesisId
  // Hook fires unconditionally; the inner `enabled` flag gates the actual fetch.
  const { data } = useHypothesis(caseId ?? '', hypothesisId)
  return data?.title
}

export function useBreadcrumb(): Breadcrumb {
  const { t } = useTranslation()
  const location = useLocation()
  const pathname = location.pathname

  // Inside a case: /cases/:caseId/... — fetch case name for the root crumb
  const caseMatch = matchPath({ path: '/cases/:caseId/*', end: false }, pathname)
  const rawCaseId = caseMatch?.params.caseId
  const caseId = rawCaseId && !RESERVED_CASE_SEGMENTS.has(rawCaseId) ? rawCaseId : undefined

  // Fire the query unconditionally (hooks must be top-level) but only actually enabled when needed.
  const caseQuery = useQuery<{ name: string }>({
    queryKey: caseId ? caseKeys.detail(caseId) : ['case', 'noop'],
    queryFn: () => api.cases.get(caseId!) as Promise<{ name: string }>,
    enabled: !!caseId,
  })

  // Investigation-detail leaf crumb — read the investigation title from the
  // per-case store. The store is module-level cached and auto-populated by
  // `<InvestigationsStoreProvider>` mounted in `CaseLayout`. Hooks must run
  // unconditionally; we just no-op the result when not on a detail route.
  const investigationLeafTitle = useInvestigationLeafTitle(caseId, pathname)
  const hypothesisLeafTitle = useHypothesisLeafTitle(caseId, pathname)
  const { mode: viewpointMode, investigationName: chipName } = useViewpointSnapshot(caseId)

  const section = getSectionForPath(pathname)
  const sectionCrumb: Crumb | undefined =
    section && !section.omitFromBreadcrumb
      ? { label: t(`nav.sections.${section.key}`), href: section.defaultRoute }
      : undefined

  if (caseId) {
    const caseName = caseQuery.data?.name ?? 'Case'
    const crumbs: Crumb[] = []
    if (sectionCrumb) crumbs.push(sectionCrumb)
    crumbs.push({ label: caseName, href: `/cases/${caseId}` })

    const rest = pathname.slice(`/cases/${caseId}`.length) // e.g. "/identity"
    const segs = rest.split('/').filter(Boolean)
    const segment = segs[0]

    // Crumbs for the current page (sub-view + optional detail leaf). Collected
    // separately so view routes can move them behind the investigation chip.
    const pageCrumbs: Crumb[] = []

    if (segment) {
      const found = findWorkspaceAndSubView(segment)
      if (found) {
        const key = pathToSubviewKey(found.subView.path)
        const translated = t(`nav.subviews.${key}`, { defaultValue: found.subView.label })
        // Sub-views with detail pages (e.g. investigations/:id, search/:id) →
        // link the parent crumb back to the list view so users can navigate up.
        const isDetailRoute =
          (segment === 'investigations' || segment === 'search') && segs.length > 1
        if (isDetailRoute) {
          pageCrumbs.push({ label: translated, href: `/cases/${caseId}/${segment}` })
        } else {
          pageCrumbs.push({ label: translated })
        }
      } else {
        pageCrumbs.push({ label: segmentToLabel(segment) })
      }
    }

    // Resolve a leaf crumb for known sub-routes that carry an entity id.
    if (segs[0] === 'investigations' && segs[1] && investigationLeafTitle) {
      pageCrumbs.push({ label: investigationLeafTitle })
    }
    if (segs[0] === 'search' && segs[1] && hypothesisLeafTitle) {
      pageCrumbs.push({ label: hypothesisLeafTitle })
    }

    // Investigation-scoped views read as pages OF the current viewpoint:
    // `Cases / case / [chip] / Graph`. Without a viewpoint (or on any other
    // page) the crumbs stay a single flat path.
    const chipInPath =
      segment !== undefined && INVESTIGATION_VIEW_SEGMENTS.has(segment) && viewpointMode !== 'empty'
    if (chipInPath) return { crumbs, viewTail: pageCrumbs }
    const leafRepeatsChip = !!chipName && pageCrumbs[pageCrumbs.length - 1]?.label === chipName
    return { crumbs: [...crumbs, ...pageCrumbs], viewTail: [], leafRepeatsChip }
  }

  // Global routes — always start with the section label, then add page-specific crumbs.
  const segs = pathname.split('/').filter(Boolean)
  const crumbs: Crumb[] = []
  if (sectionCrumb) crumbs.push(sectionCrumb)

  // Map well-known top-level segments to translation keys.
  const FRIENDLY_KEYS: Record<string, string> = {
    home: 'cases.dashboard.title',
    list: 'nav.cases.allCases',
    schema: 'nav.adminNav.schema',
    pipelines: 'nav.adminNav.pipelines',
    labels: 'nav.adminNav.labels',
    users: 'nav.adminNav.users',
    docs: 'nav.sections.docs',
    settings: 'nav.sections.settings',
    data: 'nav.sections.data',
    osint: 'extensions.osint.breadcrumb',
  }

  const segLabel = (s: string) => (FRIENDLY_KEYS[s] ? t(FRIENDLY_KEYS[s]) : segmentToLabel(s))

  // When the URL is the section root (e.g. `/docs`), show a meaningful leaf instead of
  // repeating the section name.
  const SECTION_ROOT_LEAF: Record<string, string> = {
    docs: 'nav.subviews.overview',
  }
  const isSectionRoot =
    sectionCrumb && segs.length === 1 && segLabel(segs[0]) === sectionCrumb.label
  if (isSectionRoot) {
    const leafKey = SECTION_ROOT_LEAF[segs[0]]
    if (leafKey) crumbs.push({ label: t(leafKey) })
    return { crumbs, viewTail: [] }
  }

  // Data is a flat product (data lake) — show only the section crumb, never a leaf.
  if (section?.key === 'data') {
    return { crumbs, viewTail: [] }
  }

  let href = ''
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i]
    href += `/${s}`
    // Skip a leading section-matching segment — either because it duplicates the section
    // crumb, or because the section is configured to be omitted from the breadcrumb.
    if (i === 0 && section && s === section.key) {
      continue
    }
    if (i === 0 && sectionCrumb && segLabel(s) === sectionCrumb.label) {
      continue
    }
    crumbs.push({ label: segLabel(s), href })
  }
  // Last crumb has no href (it's the current page).
  if (crumbs.length > 0) {
    crumbs[crumbs.length - 1] = { label: crumbs[crumbs.length - 1].label }
  }
  return {
    crumbs: crumbs.length > 0 ? crumbs : [{ label: t('cases.dashboard.title') }],
    viewTail: [],
  }
}
